<script setup>
import { ref, watch } from 'vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateSignature']);
const signature = ref(props.messageSignature);
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
  }
);

const {
  getSignaturePosition,
  setSignaturePosition,
  getSignatureShowSeparator,
  setSignatureShowSeparator,
} = useUISettings();

const signaturePosition = ref(getSignaturePosition());
const signatureShowSeparator = ref(getSignatureShowSeparator());

// Auto-save preferences immediately on change, decoupled from profile save
watch(signaturePosition, newVal => setSignaturePosition(newVal));
watch(signatureShowSeparator, newVal => setSignatureShowSeparator(newVal));

const updateSignature = () => {
  emit('updateSignature', signature.value);
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="updateSignature()">
    <WootMessageEditor
      id="message-signature-input"
      v-model="signature"
      class="message-editor h-[10rem] !px-3"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
      show-image-resize-toolbar
    />
    <div class="flex flex-col gap-3">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_LABEL') }}
      </span>
      <div class="flex gap-3">
        <label
          class="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border text-sm transition-colors"
          :class="
            signaturePosition === 'bottom'
              ? 'border-n-brand/50 bg-n-brand/5 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
        >
          <input
            v-model="signaturePosition"
            type="radio"
            value="bottom"
            class="hidden"
          />
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_BOTTOM') }}
        </label>
        <label
          class="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border text-sm transition-colors"
          :class="
            signaturePosition === 'top'
              ? 'border-n-brand/50 bg-n-brand/5 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
        >
          <input
            v-model="signaturePosition"
            type="radio"
            value="top"
            class="hidden"
          />
          {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION_TOP') }}
        </label>
      </div>
    </div>
    <label class="flex items-center gap-2 cursor-pointer select-none">
      <input
        v-model="signatureShowSeparator"
        type="checkbox"
        class="mt-0.5 flex-shrink-0 border-n-weak border checked:border-none checked:bg-n-brand shadow appearance-none rounded-[4px] w-4 h-4 focus:ring-1 after:content-[''] after:text-white checked:after:content-['✓'] after:flex after:items-center after:justify-center after:text-center after:text-xs after:font-bold after:relative"
      />
      <span class="text-sm text-n-slate-11">
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SHOW_SEPARATOR_LABEL') }}
      </span>
    </label>
    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
      />
    </div>
  </form>
</template>
